// frontend/src/Partnerships.js
// ============================================================
// Licensed Partnerships landing page — branded to match LandingPageV2
// ============================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const T = {
  navy:      '#10314F',
  navyLight: '#33475C',
  navyMuted: '#5A6B7D',
  gold:      '#EF9F27',
  goldDark:  '#9A5B06',
  goldLight: '#FCEBD0',
  bg:        '#F7F5F0',
  border:    '#E6E1D6',
  white:     '#FFFFFF',
  serif:     "Fraunces, Georgia, serif",
  sans:      "Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif",
};

const API_URL = "https://scholarsync-platform-production.up.railway.app";

export default function Partnerships() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', org_type: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/partnerships/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Something went wrong. Please email us at matt@yourscholarshipfinder.com.');
      }
    } catch {
      setError('Something went wrong. Please email us at matt@yourscholarshipfinder.com.');
    }
    setSubmitting(false);
  }

  const LogoSVG = () => (
    <svg width="44" height="44" viewBox="0 0 100 100" aria-hidden="true">
      <rect x="0" y="0" width="100" height="100" rx="24" fill={T.navy} />
      <path d="M50 18 C36 18 26 29 26 42 C26 58 50 84 50 84 C50 84 74 58 74 42 C74 29 64 18 50 18 Z" fill={T.white} />
      <circle cx="50" cy="41" r="8.5" fill={T.gold} />
    </svg>
  );

  return (
    <div style={{ fontFamily: T.sans, color: T.navy, background: T.bg, minHeight: '100vh' }}>
      {/* Nav */}
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 80px', background: T.white, borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 100 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
          <LogoSVG />
          <span style={{ fontFamily: T.serif, fontSize: 22, lineHeight: 1, color: T.navy }}>
            <span style={{ fontStyle: 'italic', fontWeight: 400, color: T.navyMuted }}>Your</span>{' '}
            <span style={{ fontWeight: 500 }}>Scholarship Finder</span>
          </span>
        </Link>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 36, fontSize: 15, fontWeight: 500 }}>
          <Link to="/our-story" style={{ textDecoration: 'none', color: T.navy }}>Our story</Link>
          <Link to="/partnerships" style={{ textDecoration: 'none', color: T.gold, fontWeight: 600 }}>Partnerships</Link>
          <Link to="/login" style={{ textDecoration: 'none', color: T.navy }}>Log in</Link>
          <Link to="/" style={{ background: T.navy, color: T.white, padding: '11px 20px', borderRadius: 10, fontWeight: 600, fontSize: 15, textDecoration: 'none', fontFamily: T.sans }}>
            Get started
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section style={{ background: T.navy, padding: '80px 20px 72px', textAlign: 'center' }}>
        <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.gold, marginBottom: 16 }}>
          PARTNERSHIPS
        </p>
        <h1 style={{ fontFamily: T.serif, fontWeight: 500, fontSize: 42, lineHeight: 1.12, color: T.white, margin: '0 auto 16px', maxWidth: 700 }}>
          Bulk scholarship access for schools, organizations, and donors
        </h1>
        <p style={{ fontSize: 17, lineHeight: 1.7, color: T.navyFaint || '#B9C6D4', maxWidth: 600, margin: '0 auto' }}>
          Purchase multiple licenses and give students AI-powered scholarship matching — no searching, just applying.
        </p>
      </section>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 20px' }}>

        {/* What is it */}
        <section style={{ padding: '56px 0 40px' }}>
          <h2 style={{ fontFamily: T.serif, fontWeight: 500, fontSize: 30, color: T.navy, marginBottom: 16 }}>
            What is a Licensed Partnership?
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.75, color: T.navyLight, marginBottom: 12 }}>
            A Licensed Partnership lets you purchase multiple Your Scholarship Finder
            licenses and distribute them to students — whether you're a high school counselor
            adding scholarship matching to your toolkit, an alumni association giving
            back, or a financial advisor offering college-planning services.
          </p>
          <p style={{ fontSize: 16, lineHeight: 1.75, color: T.navyLight }}>
            Each student receives a unique redemption code and creates their own
            independent account with full access to AI-powered scholarship matching,
            personalized results, and application tracking.
          </p>
        </section>

        {/* How it works */}
        <section style={{ padding: '0 0 48px' }}>
          <h2 style={{ fontFamily: T.serif, fontWeight: 500, fontSize: 30, color: T.navy, marginBottom: 24 }}>
            How It Works
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {[
              { num: '1', title: 'Contact Us', desc: 'Tell us how many licenses you need and we\'ll set up your order. Volume pricing available.' },
              { num: '2', title: 'Receive Your Codes', desc: 'After payment, you\'ll receive unique redemption codes — one per student. Share them however works best: email, printed cards, or in person.' },
              { num: '3', title: 'Students Activate', desc: 'Each student visits our site, enters their code, and creates their account. No credit card needed — they\'re in.' },
            ].map(step => (
              <div key={step.num} style={{ display: 'flex', gap: 16, alignItems: 'flex-start', background: T.white, padding: 24, borderRadius: 12, border: `1px solid ${T.border}` }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: T.navy, color: T.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, flexShrink: 0 }}>
                  {step.num}
                </div>
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, color: T.navy, marginBottom: 4, marginTop: 0 }}>{step.title}</h3>
                  <p style={{ fontSize: 15, color: T.navyLight, lineHeight: 1.65, margin: 0 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Who it's for */}
        <section style={{ padding: '0 0 48px' }}>
          <h2 style={{ fontFamily: T.serif, fontWeight: 500, fontSize: 30, color: T.navy, marginBottom: 24 }}>
            Who It's For
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {[
              { icon: '🎓', title: 'High School Counselors', desc: 'Add scholarship matching to your college-prep resources.' },
              { icon: '🏛️', title: 'Alumni Organizations', desc: 'Give students at your alma mater an edge in the scholarship search.' },
              { icon: '💼', title: 'Financial Advisors', desc: 'Offer scholarship matching as part of your college-planning service.' },
              { icon: '🤝', title: 'Community Foundations', desc: 'Help local students discover scholarships they\'d never find on their own.' },
            ].map(item => (
              <div key={item.title} style={{ background: T.white, padding: 24, borderRadius: 12, border: `1px solid ${T.border}`, textAlign: 'center' }}>
                <div style={{ fontSize: 32 }}>{item.icon}</div>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: T.navy, margin: '12px 0 8px' }}>{item.title}</h3>
                <p style={{ fontSize: 14, color: T.navyMuted, lineHeight: 1.55, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section style={{ padding: '0 0 72px' }}>
          <div style={{ background: T.white, borderRadius: 16, border: `1px solid ${T.border}`, padding: '48px 40px', textAlign: 'center' }}>
            <h2 style={{ fontFamily: T.serif, fontWeight: 500, fontSize: 30, color: T.navy, marginBottom: 8 }}>
              Ready to get started?
            </h2>
            <p style={{ fontSize: 16, color: T.navyLight, marginBottom: 32 }}>
              Tell us about your organization and how many students you'd like to support.
            </p>

            {submitted ? (
              <div style={{ padding: 32, background: T.goldLight, borderRadius: 12, maxWidth: 400, margin: '0 auto' }}>
                <p style={{ fontSize: 18, fontWeight: 600, color: T.navy, marginBottom: 8 }}>Thank you!</p>
                <p style={{ fontSize: 15, color: T.navyLight, margin: 0 }}>We'll be in touch within one business day.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ maxWidth: 480, margin: '0 auto', textAlign: 'left' }}>
                <label style={labelStyle}>Your Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Full name" style={inputStyle} />

                <label style={labelStyle}>Email Address *</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder="you@example.com" style={inputStyle} />

                <label style={labelStyle}>Phone Number</label>
                <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(303) 555-1234" style={inputStyle} />

                <label style={labelStyle}>Organization Type *</label>
                <select value={form.org_type} onChange={e => setForm({ ...form, org_type: e.target.value })} required style={inputStyle}>
                  <option value="">Select one...</option>
                  <option value="high_school_counselor">High School Counselor</option>
                  <option value="college_counselor">College Counselor</option>
                  <option value="financial_advisor">Financial Advisor</option>
                  <option value="alumni_organization">Alumni Organization</option>
                  <option value="community_foundation">Community Foundation</option>
                  <option value="other">Other</option>
                </select>

                <label style={labelStyle}>Message (optional)</label>
                <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your needs — how many students, timeline, etc." rows={4} style={{ ...inputStyle, resize: 'vertical' }} />

                {error && <p style={{ color: '#E53E3E', fontSize: 14, marginTop: 8 }}>{error}</p>}

                <button type="submit" disabled={submitting} style={{ width: '100%', marginTop: 24, padding: '14px 32px', background: T.navy, color: T.white, border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: T.sans }}>
                  {submitting ? 'Sending...' : 'Get in Touch'}
                </button>
              </form>
            )}

            <p style={{ fontSize: 14, color: T.navyMuted, marginTop: 24 }}>
              Already have a redemption code?{' '}
              <Link to="/partnerships/redeem" style={{ color: T.navy, fontWeight: 600 }}>Redeem it here →</Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#4A5568', marginBottom: 6, marginTop: 18 };
const inputStyle = { width: '100%', padding: '12px 14px', fontSize: 15, border: '1px solid #E2E8F0', borderRadius: 8, outline: 'none', boxSizing: 'border-box', fontFamily: "Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif" };
